import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../router/all_routes";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import {
  getHrLevelConfig,
  saveHrLevelConfig,
  EsepHrLevelConfig,
  EsepHrRoster,
} from "../eSepration/services/eSeparation.service";
import { isSupportViewer } from "../../../utils/permissionUtils";

const padEmpNo = (v?: string | null): string => {
  const t = (v ?? "").trim();
  return t ? t.padStart(8, "0") : "";
};

// Two fixed rosters — only the assigned employee ID varies per (roster, level). An
// employee's roster is chosen from their WING and GROUP when they apply
// (A = Executive, B = Non-Executive):
//   • O&M (OPS / MNT) non-executive          -> the first roster
//   • O&M executive, Project executive and
//     Project non-executive                  -> the second roster
const ROSTERS: { roster: EsepHrRoster; label: string; applies: string }[] = [
  {
    roster: "OM_NON_EXEC",
    label: "O&M Non-Executive",
    applies: "Employees of the OPS and MNT wings who are non-executive.",
  },
  {
    roster: "OM_EXEC_PROJ",
    label: "O&M Executive · Project Executive · Project Non-Executive",
    applies:
      "Executive employees of the OPS and MNT wings, and all employees (executive and non-executive) of the Project wing.",
  },
];
const LEVELS: {
  level_no: 1 | 2 | 3 | 4;
  role: EsepHrLevelConfig["role"];
  label: string;
}[] = [
  { level_no: 1, role: "HR_SR_CRA", label: "HR Assistant" },
  { level_no: 2, role: "HR_AM_MANAGER", label: "AM / Manager" },
  { level_no: 3, role: "HR_DY_HOD", label: "Dy. HOD" },
  { level_no: 4, role: "HR_HOD", label: "HOD HR" },
];

type CellKey = `${EsepHrRoster}-${1 | 2 | 3 | 4}`;

const EsepHrLevelConfigSettings: React.FC = () => {
  const routes = all_routes;

  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const res = await getHrLevelConfig();
    const data = res.data || [];
    const map: Record<string, string> = {};
    for (const r of data) {
      map[`${r.roster}-${r.level_no}`] = r.approver_emp_id;
    }
    setDrafts(map);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const cellValue = (key: CellKey): string => drafts[key] ?? "";

  const saveCell = async (
    roster: EsepHrRoster,
    level_no: 1 | 2 | 3 | 4,
    role: EsepHrLevelConfig["role"]
  ) => {
    const key = `${roster}-${level_no}`;
    const empId = padEmpNo(drafts[key]);
    if (!empId) return toast.warning("Employee ID is required.");
    setSavingKey(key);
    const res = await saveHrLevelConfig({
      roster,
      level_no,
      role,
      approver_emp_id: empId,
    });
    setSavingKey(null);
    if (res.success) {
      toast.success("HR level mapping saved.");
      load();
    } else {
      toast.error(res.message || "Failed to save HR level mapping.");
    }
  };

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Settings</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">App Settings</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    E-Separation HR Level Roster
                  </li>
                </ol>
              </nav>
            </div>
            <div className="head-icons ms-2">
              <CollapseHeader />
            </div>
          </div>

          <div className="row">
            <div className="col-xl-12">
              <div className="card">
                <div className="card-body">
                  <div className="border-bottom mb-3 pb-3 d-flex align-items-center justify-content-between">
                    <h4 className="mb-0">HR Level Roster (HR Assistant &rarr; AM/Manager &rarr; Dy. HOD &rarr; HOD HR)</h4>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={load}
                      disabled={loading}
                    >
                      <i className="ti ti-refresh me-1" />
                      Refresh
                    </button>
                  </div>

                  <p className="text-muted">
                    There are two fixed 4-level HR approval chains. When an
                    employee applies, the chain is chosen from their <strong>wing</strong> and
                    whether they are <strong>executive or non-executive</strong>:
                    O&amp;M non-executive employees use the first; O&amp;M executive
                    employees and all Project employees use the second. The
                    chosen chain applies to the resignation, short notice and
                    No-Dues that follow. The existing per-wing HR approver mapping
                    keeps serving requests already in progress.
                  </p>

                  {loading ? (
                    <div className="text-center py-4 text-muted">
                      <div className="spinner-border text-primary mb-2" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <div className="small">Loading roster...</div>
                    </div>
                  ) : (
                    ROSTERS.map((g) => (
                      <div key={g.roster} className="mb-4">
                        <h5 className="mb-1">{g.label}</h5>
                        <div className="text-muted small mb-2">{g.applies}</div>
                        <div className="table-responsive">
                          <table className="table align-middle mb-0">
                            <thead>
                              <tr>
                                <th style={{ width: 60 }}>Level</th>
                                <th>Designation</th>
                                <th style={{ width: 260 }}>Employee ID</th>
                                {!isSupportViewer() && (
                                  <th style={{ width: 100 }} className="text-end">
                                    &nbsp;
                                  </th>
                                )}
                              </tr>
                            </thead>
                            <tbody>
                              {LEVELS.map((lvl) => {
                                const key = `${g.roster}-${lvl.level_no}` as CellKey;
                                const isSaving = savingKey === key;
                                return (
                                  <tr key={key}>
                                    <td className="fw-medium">{lvl.level_no}</td>
                                    <td>{lvl.label}</td>
                                    <td>
                                      {isSupportViewer() ? (
                                        <span className="text-muted">
                                          {cellValue(key) || "—"}
                                        </span>
                                      ) : (
                                        <input
                                          className="form-control"
                                          inputMode="numeric"
                                          value={cellValue(key)}
                                          onChange={(e) =>
                                            setDrafts((d) => ({
                                              ...d,
                                              [key]: e.target.value,
                                            }))
                                          }
                                          onBlur={(e) =>
                                            setDrafts((d) => ({
                                              ...d,
                                              [key]: e.target.value.trim()
                                                ? padEmpNo(e.target.value)
                                                : "",
                                            }))
                                          }
                                          placeholder="8-digit employee number"
                                        />
                                      )}
                                    </td>
                                    {!isSupportViewer() && (
                                      <td className="text-end">
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-primary"
                                          disabled={isSaving}
                                          onClick={() =>
                                            saveCell(g.roster, lvl.level_no, lvl.role)
                                          }
                                        >
                                          <i className="ti ti-device-floppy me-1" />
                                          {isSaving ? "Saving..." : "Save"}
                                        </button>
                                      </td>
                                    )}
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2014 - 2025 © SmartHR.</p>
        </div>
      </div>
    </div>
  );
};

export default EsepHrLevelConfigSettings;
